import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CompanyQuery } from '@core/company/company.query';
import { LanguageService } from '@core/language/language.service';
import { AssetClassValueObject } from '@core/models/asset-class-value-object';
import { Company } from '@core/models/company.model';
import { FileObject } from '@core/models/file-object.model';
import { ProductSectionStatuses } from '@core/models/product-section-statuses.model';
import { Product } from '@core/models/product.model';
import { ValueObject } from '@core/models/value-object.model';
import { ProductQuery } from '@core/product/product.query';
import { AppStoreQuery } from '@core/state';
import { filterNil } from '@datorama/akita';
import { FormOption } from '@feature/forms/form-controls/form-option';
import {
  mapIfDefined,
  normalizeFormModelValues,
  FormModel,
  TypedFormControls,
  TypedFormGroup,
} from '@feature/forms/form-model';
import { AsyncFormService } from '@feature/forms/form-service';
import {
  longTextMaxLength,
  mediumTextMaxLength,
  shortTextMaxLength,
  WaiValidators,
} from '@feature/forms/wai-validators';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { combineLatest, BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { merge } from 'rxjs';
import { normalizeDate } from '@core/utils';

export type ProductFormControls = TypedFormControls<Product>;
export type ProductFormGroup = TypedFormGroup<Product>;
export type ProductFormModel = FormModel<Product> & { emittentCompany: string };

const issuePricePerUnitMaxValue = 100000000;
const investmentNumberMaxLength = 12;

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class ProductFormService implements AsyncFormService<Product> {
  public assetClasses: Observable<FormOption[]> = this.appStoreQuery
    .selectAvailableAssetClasses()
    .pipe(map((items) => items?.map(FormOption.fromValueObject)));

  public assetSubclasses: Observable<FormOption[]> = null;

  public emittentCompanies: Observable<FormOption[]> = this.emittentCompanyQuery.selectAll().pipe(
    filterNil,
    map((companies) => companies.map(FormOption.fromCompany)),
  );

  public form: Observable<ProductFormGroup>;

  public industries: Observable<FormOption[]> = this.appStoreQuery.selectAvailableIndustries().pipe(
    filterNil,
    map((items) => items.map(FormOption.fromValueObject)),
  );

  public investorClasses: Observable<FormOption[]> = this.appStoreQuery.selectAvailableClientClasses().pipe(
    filterNil,
    map((items) => items.map(FormOption.fromValueObject)),
  );

  public ratings: Observable<FormOption[]> = this.appStoreQuery.selectAvailableRatings().pipe(
    filterNil,
    map((items) => items.map(FormOption.fromValueObject)),
  );

  public countries: Observable<FormOption[]> = this.appStoreQuery.selectAvailableCountries().pipe(
    filterNil,
    map((items) => items.map(FormOption.fromValueObject)),
  );

  private formEnabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  private formGroup: ProductFormGroup = null;

  private selectedAssetClass: BehaviorSubject<AssetClassValueObject['codename']> = new BehaviorSubject<
    AssetClassValueObject['codename']
  >(null);
  public durationOptions = ['10-years', '10-years-plus-3x1', '10-years-plus-2x1'];

  private getCurrentLanguageId(): number {
    const currentLanguageCode = this.languageService.getCurrentLanguage();
    const availableLanguages = this.languageService.getAvailableLanguages();

    // Find the language object that matches the current language code
    const currentLanguage = availableLanguages.find((lang) => lang.code === currentLanguageCode);

    if (currentLanguage && currentLanguage.id) {
      return currentLanguage.id;
    }

    // Fallback to common language ID mappings if language is not found in available languages
    const languageCodeMap: { [key: string]: number } = {
      en: 1, // English
      de: 2, // German
      fr: 3, // French
      es: 4, // Spanish
      it: 5, // Italian
      pt: 6, // Portuguese
      nl: 7, // Dutch
      pl: 8, // Polish
      ru: 9, // Russian
      zh: 10, // Chinese
      ja: 11, // Japanese
      ko: 12, // Korean
    };

    const fallbackId = languageCodeMap[currentLanguageCode];
    if (fallbackId) {
      return fallbackId;
    }

    // Ultimate fallback to English ID 1 if nothing else works
    return 1;
  }

  constructor(
    private appStoreQuery: AppStoreQuery,
    private emittentCompanyQuery: CompanyQuery,
    private productQuery: ProductQuery,
    private languageService: LanguageService,
  ) {
    this.form = this.productQuery.selectActiveId().pipe(
      filterNil,
      switchMap((pid) => this.productQuery.selectEntity(pid)),
      filterNil,
      withLatestFrom(this.appStoreQuery.selectUser()),
      map(([product, user]) => this.buildFormConditionally(product, user?.isStaff)),
      tap((form) => {
        // Ensure default asset class is set for private products if still missing
        if (form.controls.productMarketPlacement.value === 'private' && !form.controls.assetClass.value) {
          form.controls.assetClass.setValue('private-placement');
          this.selectedAssetClass.next('private-placement');
        } else {
          this.selectedAssetClass.next(this.formGroup.controls.assetClass.value);
        }
        this.setupValueChangeObserver(form);
        this.formEnabled
          .pipe(untilDestroyed(this))
          .subscribe((isEnabled) => (isEnabled ? form.enable() : form.disable()));
      }),
    );

    const currentSelectedAssetClass: Observable<string> = this.selectedAssetClass.asObservable();
    const currentFormEnabled: Observable<boolean> = this.formEnabled.asObservable();

    this.assetSubclasses = combineLatest([currentSelectedAssetClass, currentFormEnabled]).pipe(
      switchMap(([parentCodename, formEnabled]) =>
        this.appStoreQuery.selectAvailableAssetClasses(parentCodename).pipe(
          tap((assetSubclasses) => {
            if (parentCodename && assetSubclasses.length > 0 && formEnabled) {
              this.formGroup.controls.assetSubclass.enable();
            } else {
              this.formGroup.controls.assetSubclass.disable();
            }
          }),
          map((items) => items.map(FormOption.fromValueObject)),
        ),
      ),
    );
  }

  public disableForm(): void {
    this.formEnabled.next(false);
  }

  public enableForm(): void {
    this.formEnabled.next(true);
  }

  public relaxValidatorsForPartialSave(): void {
    if (!this.formGroup) return;

    // Remove validators from all fields that were blocking saves
    const fieldsToRelax = [
      'assetClass',
      'assetSubclass',
      'emittentCompany',
      'industries',
      'productName',
      'productNumber',
      'defaultLanguage',
      'country',
      'permittedInvestorClasses',
      'maxInvestment',
      'raisedFunds',
      'minInvestment',
      'fundStructure',
      'lockInPeriod',
      'fundInvestmentMethod',
      'targetReturn',
    ];

    fieldsToRelax.forEach((fieldName) => {
      const control = this.formGroup.get(fieldName);
      if (control) {
        // Clear all validators that were blocking saves
        control.clearValidators();

        // Keep only format/range validators that don't prevent saving
        const newValidators = [];

        // Re-add only non-blocking validators based on field type
        switch (fieldName) {
          case 'maxInvestment':
          case 'raisedFunds':
          case 'minInvestment':
            newValidators.push(Validators.maxLength(12));
            break;
          case 'productName':
            newValidators.push(Validators.maxLength(150));
            break;
          case 'productNumber':
            newValidators.push(Validators.maxLength(30));
            break;
          case 'lockInPeriod':
            newValidators.push(Validators.min(0), Validators.max(99.99));
            break;
          case 'fundStructure':
            newValidators.push(Validators.minLength(3), Validators.maxLength(20));
            break;
          case 'targetReturn':
            newValidators.push(Validators.minLength(3), Validators.maxLength(20));
            break;
        }

        if (newValidators.length > 0) {
          control.setValidators(newValidators);
        }
        control.updateValueAndValidity();
      }
    });
  }

  public validateForIssueStart(): boolean {
    if (!this.formGroup) return false;

    // Check the required fields for issue starting (matching backend is_complete logic)
    const requiredFieldsForIssue = [
      'productName',
      'emittentCompany',
      'assetClass',
      'country',
      'industries',
      'permittedInvestorClasses',
    ];

    // Check if all required fields have values
    const allRequiredFieldsFilled = requiredFieldsForIssue.every((fieldName) => {
      const control = this.formGroup.get(fieldName);
      const value = control?.value;

      // For arrays (like industries, permittedInvestorClasses), check if they have items
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      // For other fields, check if they have a truthy value
      return value !== null && value !== undefined && value !== '';
    });

    // Check asset subclass requirement (only if asset class has subclasses)
    const assetClassValue = this.formGroup.get('assetClass')?.value;
    const assetSubclassValue = this.formGroup.get('assetSubclass')?.value;

    // If we have an asset class, we might need to check if subclasses are required
    // For now, we'll assume if assetClass is set, assetSubclass is required
    const assetSubclassComplete = !assetClassValue || assetSubclassValue;

    return allRequiredFieldsFilled && assetSubclassComplete;
  }

  /** @deprecated */
  public getForm(): ProductFormGroup {
    return this.formGroup;
  }

  public selectAssetClass(assetClass: AssetClassValueObject['codename']): void {
    this.selectedAssetClass.next(assetClass);
    this.formGroup.controls.assetSubclass.setValue(null);
  }

  public toModel(formModel: ProductFormModel): Product {
    formModel = normalizeFormModelValues(formModel) as ProductFormModel;
    const normalizedFormModel = formModel as ProductFormModel;
    return Product.create({
      ...normalizedFormModel,
      sectionStatuses: mapIfDefined(normalizedFormModel.sectionStatuses, () =>
        ProductSectionStatuses.create(normalizedFormModel.sectionStatuses),
      ),
      assetClass: mapIfDefined(normalizedFormModel.assetClass, () =>
        AssetClassValueObject.create({ codename: normalizedFormModel.assetClass }),
      ),
      assetSubclass: mapIfDefined(normalizedFormModel.assetSubclass, () =>
        AssetClassValueObject.create({ codename: normalizedFormModel.assetSubclass }),
      ),
      keepPrivate: !!normalizedFormModel.keepPrivate,
      clientCompany: normalizedFormModel.keepPrivate ? normalizedFormModel.clientCompany ?? null : null,
      emittentCompany: mapIfDefined(normalizedFormModel.emittentCompany, () =>
        Company.create({ id: normalizedFormModel.emittentCompany }),
      ),
      image: mapIfDefined(normalizedFormModel.image, () => FileObject.create(normalizedFormModel.image)),
      bannerImage: mapIfDefined(normalizedFormModel.bannerImage, () =>
        FileObject.create(normalizedFormModel.bannerImage),
      ),
      industries: mapIfDefined(normalizedFormModel.industries, () =>
        normalizedFormModel.industries.map((i) => ValueObject.create({ codename: i })),
      ),
      investmentCategories: mapIfDefined(normalizedFormModel.investmentCategories, () =>
        normalizedFormModel.investmentCategories.map((ic) => ValueObject.create({ codename: ic })),
      ),
      permittedInvestorClasses: mapIfDefined(normalizedFormModel.permittedInvestorClasses, () =>
        normalizedFormModel.permittedInvestorClasses.map((pic) => ValueObject.create({ codename: pic })),
      ),
      processDescription: normalizedFormModel.processDescription ?? '',
      rating: mapIfDefined(normalizedFormModel.rating, () =>
        ValueObject.create({ codename: normalizedFormModel.rating }),
      ),
      country: mapIfDefined(normalizedFormModel.country, () =>
        ValueObject.create({ codename: normalizedFormModel.country }),
      ),
      defaultLanguage: normalizedFormModel.defaultLanguage,
      shortProfileDescription: normalizedFormModel.shortProfileDescription ?? '',
      termUntil: mapIfDefined<string, string>(normalizedFormModel.termUntil, () =>
        normalizeDate(normalizedFormModel.termUntil),
      ),
      actualReturnDate:
        normalizedFormModel.fundType === 'open'
          ? mapIfDefined<string, string>(normalizedFormModel.actualReturnDate, () =>
              normalizeDate(normalizedFormModel.actualReturnDate),
            )
          : null,
      targetReturn: normalizedFormModel.targetReturn ?? null,
      indicativeAnnualReturn: normalizedFormModel.indicativeAnnualReturn ?? null,
    });
  }

  public buildForm(product: Product): ProductFormGroup {
    this.formGroup = new FormGroup(
      {
        uid: new FormControl(product.uid),
        assetClass: new FormControl(product.assetClass?.codename, [Validators.required, WaiValidators.nonEmpty]),
        assetSubclass: new FormControl(product.assetSubclass?.codename, [Validators.required, WaiValidators.nonEmpty]),
        keepPrivate: new FormControl(!!product.keepPrivate),
        clientCompany: new FormControl(product.clientCompany ?? null),
        emittentCompany: new FormControl(product.emittentCompany?.id, [Validators.required, WaiValidators.nonEmpty]),
        id: new FormControl(product.id),
        image: new FormControl(product.image),
        bannerImage: new FormControl(product.bannerImage),
        industries: new FormControl(
          product.industries?.map((item) => item.codename),
          [Validators.required],
        ),
        investmentCategories: new FormControl(product.investmentCategories?.map((item) => item.codename)),
        esgCompliance: new FormControl(product.esgCompliance),
        maxInterestPerDividendOnIssue: new FormControl(product.maxInterestPerDividendOnIssue, [
          Validators.maxLength(5),
          Validators.min(1),
          Validators.max(100),
          WaiValidators.nonEmpty,
        ]),
        maxInvestment: new FormControl(product.maxInvestment, [
          Validators.maxLength(investmentNumberMaxLength),
          WaiValidators.nonEmpty,
          Validators.required,
        ]),
        raisedFunds: new FormControl(product.raisedFunds, [
          Validators.maxLength(investmentNumberMaxLength),
          Validators.required,
          WaiValidators.nonEmpty,
        ]),
        availableFunds: new FormControl(product.availableFunds),
        maxIssuePricePerUnit: new FormControl(product.maxIssuePricePerUnit, [
          Validators.max(issuePricePerUnitMaxValue),
          Validators.maxLength(investmentNumberMaxLength),
          WaiValidators.nonEmpty,
        ]),
        maxIssueValue: new FormControl(product.maxIssueValue, [WaiValidators.nonEmpty]),
        maxRate: new FormControl(product.maxRate, [
          Validators.maxLength(5),
          Validators.min(1),
          Validators.max(100),
          WaiValidators.nonEmpty,
        ]),
        minInterestPerDividendOnIssue: new FormControl(product.minInterestPerDividendOnIssue, [
          Validators.maxLength(5),
          Validators.min(1),
          Validators.max(100),
          WaiValidators.nonEmpty,
        ]),
        minInvestment: new FormControl(product.minInvestment, [
          Validators.maxLength(investmentNumberMaxLength),
          WaiValidators.nonEmpty,
          Validators.required,
        ]),
        minIssuePricePerUnit: new FormControl(product.minIssuePricePerUnit, [
          Validators.max(issuePricePerUnitMaxValue),
          Validators.maxLength(investmentNumberMaxLength),
          WaiValidators.nonEmpty,
        ]),
        minIssueValue: new FormControl(product.minIssueValue, [WaiValidators.nonEmpty]),
        minRate: new FormControl(product.minRate, [
          Validators.maxLength(5),
          Validators.min(1),
          Validators.max(100),
          WaiValidators.nonEmpty,
        ]),
        productName: new FormControl(product.productName, [
          Validators.maxLength(mediumTextMaxLength),
          WaiValidators.nonEmpty,
          Validators.required,
        ]),
        nominalNumberOfUnits: new FormControl(product.nominalNumberOfUnits, [
          Validators.maxLength(investmentNumberMaxLength),
          WaiValidators.nonEmpty,
        ]),
        nominalUnitValue: new FormControl(product.nominalUnitValue, [
          Validators.maxLength(investmentNumberMaxLength),
          WaiValidators.nonEmpty,
        ]),
        nominalValueDuringIssue: new FormControl(product.nominalValueDuringIssue, [
          Validators.maxLength(investmentNumberMaxLength),
          WaiValidators.nonEmpty,
        ]),
        numberOfUnitsDuringIssue: new FormControl(product.numberOfUnitsDuringIssue, [
          Validators.maxLength(investmentNumberMaxLength),
          WaiValidators.nonEmpty,
        ]),
        numberOfUnitsIssued: new FormControl(product.numberOfUnitsIssued, [
          Validators.maxLength(investmentNumberMaxLength),
          WaiValidators.nonEmpty,
        ]),
        permittedInvestorClasses: new FormControl(
          product.permittedInvestorClasses?.map((item) => item.codename),
          [Validators.required],
        ),
        processDescription: new FormControl(product.processDescription, [
          WaiValidators.maxVisibleTextLength(longTextMaxLength),
          WaiValidators.nonEmpty,
        ]),
        productNumber: new FormControl(product.productNumber, [
          Validators.maxLength(shortTextMaxLength),
          WaiValidators.nonEmpty,
        ]),
        defaultLanguage: new FormControl(product.defaultLanguage || this.getCurrentLanguageId()),
        rating: new FormControl(product.rating?.codename),
        country: new FormControl(product.country?.codename, [Validators.required, WaiValidators.nonEmpty]),
        shortProfileDescription: new FormControl(product.shortProfileDescription, [
          WaiValidators.maxVisibleTextLength(longTextMaxLength),
          WaiValidators.nonEmpty,
        ]),
        sectionStatuses: new FormControl(product.sectionStatuses),
        status: new FormControl(product.status),
        termUntil: new FormControl(product.termUntil, [WaiValidators.nonEmpty]),
        duration: new FormControl(
          product.duration ? (this.durationOptions.includes(product.duration) ? product.duration : 'other') : '',
          [WaiValidators.nonEmpty],
        ),
        releaseDate: new FormControl(product.releaseDate),

        customDuration: new FormControl(this.durationOptions.includes(product.duration) ? '' : product.duration),
        totalNominalValue: new FormControl(product.totalNominalValue, [WaiValidators.nonEmpty]),
        totalNominalValueAfterIssue: new FormControl(product.totalNominalValueAfterIssue, [WaiValidators.nonEmpty]),
        useFixedInterestsModel: new FormControl(product.useFixedInterestsModel),
        useFixedRateModel: new FormControl(product.useFixedRateModel),
        isFeaturedEligible: new FormControl(!!product.isFeaturedEligible),
        productMarketPlacement: new FormControl(product.productMarketPlacement),
        initialInvestmentVolume: new FormControl(product.initialInvestmentVolume, [
          Validators.maxLength(12),
          WaiValidators.maxInitialInvestmentVolume(99000000000), // 99 billion limit
        ]),
        currentInvestmentVolume: new FormControl(product.currentInvestmentVolume),
        minimumIncrement: new FormControl(product.minimumIncrement, [Validators.maxLength(12)]),
        maximumLoss: new FormControl(product.maximumLoss),
        paymentType: new FormControl(product.paymentType),
        indicativeAnnualReturn: new FormControl(product.indicativeAnnualReturn),
        fundType: new FormControl(product.fundType),

        fundInvestmentMethod: new FormControl(product.fundInvestmentMethod, [Validators.required]),

        fundStructure: new FormControl(product.fundStructure, [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(20),
        ]),
        lockInPeriod: new FormControl(product.lockInPeriod, [
          Validators.required,
          Validators.min(0),
          Validators.max(99.99),
        ]),
        liquidityRestrictions: new FormControl(product.liquidityRestrictions, Validators.maxLength(200)),

        riskClassification: new FormControl(product.riskClassification || 'high'),
        actualReturnDate: new FormControl(product?.actualReturnDate || null),
        targetReturn: new FormControl(product?.targetReturn || null, [
          Validators.minLength(3),
          Validators.maxLength(20),
        ]),
        issuingId: new FormControl(product?.issuingId || null),
        canInvest: new FormControl({ value: product.canInvest, disabled: true }),
        denialReason: new FormControl({ value: product.denialReason ?? null, disabled: true }),
        ddqSummary: new FormControl(product?.ddqSummary || null),
        availableTranslations: new FormControl({ value: product?.availableTranslations || [], disabled: true }),
      } as ProductFormControls,
      [
        WaiValidators.compare('minInterestPerDividendOnIssue', 'le', 'maxInterestPerDividendOnIssue'),
        WaiValidators.compare('minInvestment', 'le', 'maxInvestment'),
        WaiValidators.compare('minIssuePricePerUnit', 'le', 'maxIssuePricePerUnit'),
        WaiValidators.compare('minIssueValue', 'le', 'maxIssueValue'),
        WaiValidators.compare('minRate', 'le', 'maxRate'),
        WaiValidators.compareToSum('numberOfUnitsIssued', 'le', 'numberOfUnitsDuringIssue', 'nominalNumberOfUnits'),
      ],
    ) as ProductFormGroup;

    // --- Ensure correct initial state for targetReturn, indicativeReturn, and termUntil ---
    const fundType = product.fundType;
    const targetReturnControl = this.formGroup.get('targetReturn');
    const indicativeReturnControl = this.formGroup.get('indicativeAnnualReturn');
    const termUntilControl = this.formGroup.get('termUntil');

    if (fundType === 'open') {
      targetReturnControl.enable();
      targetReturnControl.setValidators([Validators.minLength(3), Validators.maxLength(20)]);
      targetReturnControl.setValue(product.targetReturn);
      indicativeReturnControl.clearValidators();
      termUntilControl.clearValidators();
      termUntilControl.setValue(null);
    } else {
      targetReturnControl.disable();
      targetReturnControl.clearValidators();
      targetReturnControl.setValue(null);
      indicativeReturnControl.clearValidators();
      termUntilControl.setValidators([WaiValidators.nonEmpty]);
    }
    targetReturnControl.updateValueAndValidity();
    indicativeReturnControl.updateValueAndValidity();
    termUntilControl.updateValueAndValidity();
    // --- End initial state logic ---

    this.formGroup
      .get('fundType')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((fundType) => {
        const termUntilControl = this.formGroup.get('termUntil');
        const indicativeReturnControl = this.formGroup.get('indicativeAnnualReturn');
        const targetReturnControl = this.formGroup.get('targetReturn');

        if (fundType === 'open') {
          // For open funds: clear term until, actualReturnDate is optional
          termUntilControl.clearValidators();
          termUntilControl.setValue(null);
          indicativeReturnControl.clearValidators();
        } else {
          // For closed funds: require term until, clear actual return date
          termUntilControl.setValidators([WaiValidators.nonEmpty]);
          targetReturnControl.setValue(null);
          targetReturnControl.disable();
          indicativeReturnControl.clearValidators();
        }

        // Update validation state for all controls
        termUntilControl.updateValueAndValidity();
        indicativeReturnControl.updateValueAndValidity();
        targetReturnControl.updateValueAndValidity();
      });

    // Set up initial validation state based on the fund type when form is created
    const initialFundType = this.formGroup.get('fundType').value;
    if (initialFundType === 'open') {
      // For initial open fund: actualReturnDate is optional, clear term until
      this.formGroup.get('actualReturnDate').clearValidators();
      this.formGroup.get('termUntil').clearValidators();
      this.formGroup.get('termUntil').setValue(null);
    } else {
      // For initial closed fund: require term until, clear actual return date
      this.formGroup.get('actualReturnDate').clearValidators();
      this.formGroup.get('actualReturnDate').setValue(null);
      this.formGroup.get('termUntil').setValidators([WaiValidators.nonEmpty]);
    }
    // Update initial validation state
    this.formGroup.get('actualReturnDate').updateValueAndValidity();
    this.formGroup.get('termUntil').updateValueAndValidity();

    // Subscribe to both termUntil and duration changes using RxJS merge
    // This ensures mutual exclusivity with actualReturnDate
    merge(this.formGroup.get('termUntil').valueChanges, this.formGroup.get('duration').valueChanges)
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        // If either field changes and has a value, and fund is closed
        // Clear actual return date to maintain data consistency
        if (value && this.formGroup.get('fundType').value === 'closed') {
          this.formGroup.get('actualReturnDate').setValue(null);
        }
      });

    // Subscribe to actual return date changes
    this.formGroup
      .get('actualReturnDate')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        // If actual return date is set and fund is open
        // Clear both termUntil and duration to maintain data consistency
        if (value && this.formGroup.get('fundType').value === 'open') {
          this.formGroup.get('termUntil').setValue(null);
          this.formGroup.get('duration').setValue(null);
        }
      });

    this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe((vc) => {
      // perform calculations
      const totalNominalValue = vc.nominalUnitValue * vc.nominalNumberOfUnits;
      const nominalValueDuringIssue = vc.nominalUnitValue * vc.numberOfUnitsDuringIssue;
      const totalNominalValueAfterIssue = totalNominalValue + nominalValueDuringIssue;
      const minIssueValue = vc.minIssuePricePerUnit * vc.numberOfUnitsIssued;
      const maxIssueValue = vc.maxIssuePricePerUnit * vc.numberOfUnitsIssued;

      // update the form
      this.formGroup.patchValue(
        {
          totalNominalValue,
          nominalValueDuringIssue,
          totalNominalValueAfterIssue,
          minIssueValue,
          maxIssueValue,
        },
        { emitEvent: false },
      );

      // run custom validation
      this.validateMaxLength('totalNominalValue', totalNominalValue);
      this.validateMaxLength('nominalValueDuringIssue', nominalValueDuringIssue);
      this.validateMaxLength('totalNominalValueAfterIssue', totalNominalValueAfterIssue);
      this.validateMaxLength('minIssueValue', minIssueValue);
      this.validateMaxLength('maxIssueValue', maxIssueValue);
    });

    // Set default asset class for private products if not already set
    if (product.productMarketPlacement === 'private') {
      const productAssetClass = product.assetClass?.codename;
      const formAssetClass = this.formGroup.controls.assetClass.value;
      const currentAssetClass = formAssetClass || productAssetClass;

      if (!currentAssetClass || currentAssetClass === '') {
        this.formGroup.controls.assetClass.setValue('private-placement');
        this.selectedAssetClass.next('private-placement');
      }
    }

    // Relax validators to allow partial saves
    this.relaxValidatorsForPartialSave();

    return this.formGroup;
  }

  public buildFormConditionally(product: Product, forSuperUser = false): ProductFormGroup {
    this.formGroup = this.buildForm(product);

    if (!forSuperUser) {
      this.formGroup.controls.emittentCompany.disable();
    }

    return this.formGroup;
  }

  private validateMaxLength(fieldName: string, currentValue: number): void {
    const length = currentValue.toString().length;

    if (length > investmentNumberMaxLength) {
      const field = this.formGroup.get(fieldName);
      field.setErrors({
        maxlength: {
          requiredLength: investmentNumberMaxLength,
          actualLength: length,
        },
      });
      field.markAsTouched();
    }
  }

  private setupValueChangeObserver(form: TypedFormGroup<Product>): void {
    // Subscribe to changes in fund type to handle validation rules
    form
      .get('fundType')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((fundType) => {
        const actualReturnDateControl = form.get('actualReturnDate');
        const termUntilControl = form.get('termUntil');
        const targetReturnControl = form.get('targetReturn');
        const indicativeReturnControl = form.get('indicativeAnnualReturn');

        if (fundType === 'open') {
          // For open funds: actualReturnDate is optional, clear term until
          actualReturnDateControl.clearValidators();
          termUntilControl.clearValidators();
          termUntilControl.setValue(null);

          // Enable target return for open funds (not required)
          targetReturnControl.enable();
          targetReturnControl.setValidators([Validators.minLength(3), Validators.maxLength(20)]);

          // Make indicative return optional for open funds
          indicativeReturnControl.clearValidators();
        } else {
          // For closed funds: require term until, clear actual return date
          actualReturnDateControl.clearValidators();
          actualReturnDateControl.setValue(null);
          termUntilControl.setValidators([WaiValidators.nonEmpty]);

          // Clear and disable target return for closed funds
          targetReturnControl.setValue(null);
          targetReturnControl.disable();
          targetReturnControl.clearValidators();

          // Make indicative return optional for closed funds too
          indicativeReturnControl.clearValidators();
        }
        // Update validation state for all controls
        actualReturnDateControl.updateValueAndValidity();
        termUntilControl.updateValueAndValidity();
        targetReturnControl.updateValueAndValidity();
        indicativeReturnControl.updateValueAndValidity();
      });

    form.valueChanges.pipe(untilDestroyed(this)).subscribe((vc) => {
      // perform calculations
      const totalNominalValue = vc.nominalUnitValue * vc.nominalNumberOfUnits;
      const nominalValueDuringIssue = vc.nominalUnitValue * vc.numberOfUnitsDuringIssue;
      const totalNominalValueAfterIssue = totalNominalValue + nominalValueDuringIssue;
      const minIssueValue = vc.minIssuePricePerUnit * vc.numberOfUnitsIssued;
      const maxIssueValue = vc.maxIssuePricePerUnit * vc.numberOfUnitsIssued;

      // update the form
      form.patchValue(
        {
          totalNominalValue,
          nominalValueDuringIssue,
          totalNominalValueAfterIssue,
          minIssueValue,
          maxIssueValue,
        },
        { emitEvent: false },
      );

      // run custom validation
      this.validateMaxLength('totalNominalValue', totalNominalValue);
      this.validateMaxLength('nominalValueDuringIssue', nominalValueDuringIssue);
      this.validateMaxLength('totalNominalValueAfterIssue', totalNominalValueAfterIssue);
      this.validateMaxLength('minIssueValue', minIssueValue);
      this.validateMaxLength('maxIssueValue', maxIssueValue);
    });
  }
}
